import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import api from '../services/api';
import './LevelForm.css';

const LevelForm = ({ initialData, onSubmit, onCancel, isLoading, parentLevel = null, mode = 'level' }) => {
  // mode: 'level' (crear nivel/categoría), 'sublevel' (crear subnivel de una categoría)
  const { register, handleSubmit, watch, reset, setValue, getValues, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      description: '',
      order: 0,
      discipline_id: parentLevel?.discipline_id || '',
      category_type: mode === 'sublevel' ? 'skill' : 'skill',
      parent_level_id: parentLevel?.id || null,
      min_birth_year: null,
      max_birth_year: null,
      min_age: null,
      max_age: null,
    },
  });

  const categoryType = watch('category_type');
  const disciplineId = watch('discipline_id');

  const validateBirthYearRange = (field) => (value) => {
    const min = field === 'min_birth_year' ? value : getValues('min_birth_year');
    const max = field === 'max_birth_year' ? value : getValues('max_birth_year');
    if (min != null && max != null && min > max) {
      return 'Rango invertido: el año máximo debe ser mayor o igual al mínimo (ej: desde 2014 hasta 2016)';
    }
    return true;
  };

  const validateAgeRange = (field) => (value) => {
    const min = field === 'min_age' ? value : getValues('min_age');
    const max = field === 'max_age' ? value : getValues('max_age');
    if (min != null && max != null && min > max) {
      return 'Rango invertido: la edad máxima debe ser mayor o igual a la mínima (ej: desde 10 hasta 12)';
    }
    return true;
  };

  const onFormSubmit = (data) => {
    const birthYearError = validateBirthYearRange('max_birth_year')(data.max_birth_year);
    const ageError = validateAgeRange('max_age')(data.max_age);

    if (birthYearError !== true) {
      setError('min_birth_year', { type: 'manual', message: birthYearError });
      setError('max_birth_year', { type: 'manual', message: birthYearError });
      return;
    }
    clearErrors(['min_birth_year', 'max_birth_year']);

    if (ageError !== true) {
      setError('min_age', { type: 'manual', message: ageError });
      setError('max_age', { type: 'manual', message: ageError });
      return;
    }
    clearErrors(['min_age', 'max_age']);

    onSubmit(data);
  };

  // Actualizar formulario cuando initialData cambie
  useEffect(() => {
    if (initialData) {
      reset({
        ...initialData,
        // Asegurar que parent_level_id siempre se preserve para subniveles
        parent_level_id: initialData.parent_level_id || (initialData.parent?.id || null),
        min_birth_year: initialData.min_birth_year || null,
        max_birth_year: initialData.max_birth_year || null,
        min_age: initialData.min_age || null,
        max_age: initialData.max_age || null,
      });
    }
  }, [initialData, reset]);

  // Si es modo subnivel, establecer valores del padre
  useEffect(() => {
    if (mode === 'sublevel' && parentLevel) {
      setValue('discipline_id', parentLevel.discipline_id);
      setValue('parent_level_id', parentLevel.id);
      setValue('category_type', 'skill'); // Subniveles siempre son de habilidad
    }
  }, [mode, parentLevel, setValue]);

  // Obtener disciplinas
  const { data: disciplines } = useQuery('disciplines', async () => {
    const response = await api.get('/disciplines');
    return response.data;
  });

  // Obtener categorías padre disponibles (solo para modo level cuando se selecciona skill)
  const { data: parentCategories } = useQuery(
    ['parent-categories', disciplineId],
    async () => {
      if (!disciplineId) return [];
      const response = await api.get('/levels', { 
        params: { 
          discipline_id: disciplineId,
          only_parents: true,
          is_active: true
        } 
      });
      return response.data;
    },
    { enabled: !!disciplineId && mode === 'level' && categoryType === 'skill' }
  );

  // Obtener niveles existentes para calcular el orden automático
  const { data: existingLevels } = useQuery(
    ['existing-levels', disciplineId, categoryType, parentLevel?.id],
    async () => {
      if (!disciplineId) return [];
      const params = { 
        discipline_id: disciplineId,
        is_active: true
      };
      
      // Si es subnivel, obtener solo subniveles del mismo padre
      if (mode === 'sublevel' && parentLevel?.id) {
        params.parent_level_id = parentLevel.id;
      } else if (mode === 'level') {
        // Si es nivel raíz, obtener solo niveles sin padre
        params.only_root = true;
      }
      
      const response = await api.get('/levels', { params });
      return response.data || [];
    },
    { 
      enabled: !!disciplineId && !initialData, // Solo calcular si no estamos editando
      keepPreviousData: true
    }
  );

  // Función para obtener orden sugerido basado en el nombre del nivel
  const getSuggestedOrderFromName = (name) => {
    if (!name) return null;
    const nameLower = name.toLowerCase().trim();
    
    // Mapeo de nombres comunes a orden
    const orderMap = {
      'principiante': 0,
      'basico': 0,
      'inicial': 0,
      'nivel 1': 0,
      'nivel i': 0,
      'intermedio': 1,
      'medio': 1,
      'nivel 2': 1,
      'nivel ii': 1,
      'avanzado': 2,
      'experto': 3,
      'nivel 3': 2,
      'nivel iii': 2,
      'competitivo': 2,
      'entrenamiento': 0,
    };
    
    // Buscar coincidencia exacta o parcial
    for (const [key, order] of Object.entries(orderMap)) {
      if (nameLower.includes(key)) {
        return order;
      }
    }
    
    return null;
  };

  // Calcular y establecer orden automático cuando cambia la disciplina, tipo o nombre
  const levelName = watch('name');
  useEffect(() => {
    if (!initialData && disciplineId && existingLevels) {
      // Filtrar niveles del mismo tipo
      const sameTypeLevels = existingLevels.filter(level => {
        if (mode === 'sublevel') {
          return level.parent_level_id === parentLevel?.id;
        } else {
          return level.category_type === categoryType && !level.parent_level_id;
        }
      });
      
      // Intentar obtener orden sugerido del nombre
      let suggestedOrder = null;
      if (levelName) {
        suggestedOrder = getSuggestedOrderFromName(levelName);
      }
      
      // Si hay un orden sugerido y no está ocupado, usarlo
      if (suggestedOrder !== null) {
        const orderTaken = sameTypeLevels.some(l => l.order === suggestedOrder);
        if (!orderTaken) {
          setValue('order', suggestedOrder);
          return;
        }
      }
      
      // Si no hay orden sugerido o está ocupado, calcular el siguiente orden disponible
      const maxOrder = sameTypeLevels.length > 0 
        ? Math.max(...sameTypeLevels.map(l => l.order || 0))
        : -1;
      
      const nextOrder = maxOrder + 1;
      
      // Solo actualizar si el orden actual es 0 (valor por defecto)
      const currentOrder = watch('order');
      if (currentOrder === 0 || currentOrder === null || currentOrder === undefined) {
        setValue('order', suggestedOrder !== null && suggestedOrder <= nextOrder ? suggestedOrder : nextOrder);
      }
    }
  }, [disciplineId, categoryType, existingLevels, mode, parentLevel, initialData, setValue, watch, levelName]);

  const isEditingCategory = initialData && initialData.category_type === 'age' && !initialData.parent_level_id;
  const isEditingSublevel = initialData && initialData.parent_level_id;

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="level-form">
      {/* Indicador de modo */}
      {mode === 'sublevel' && parentLevel && (
        <div className="mode-indicator sublevel-mode">
          <strong>📂 Creando subnivel para:</strong>
          <span className="parent-name">{parentLevel.name}</span>
          {parentLevel.min_birth_year && (
            <span className="parent-info">
              (Año: {parentLevel.min_birth_year}{parentLevel.max_birth_year && parentLevel.max_birth_year !== parentLevel.min_birth_year ? `-${parentLevel.max_birth_year}` : ''})
            </span>
          )}
          {parentLevel.min_age && (
            <span className="parent-info">
              (Edad: {parentLevel.min_age}{parentLevel.max_age ? `-${parentLevel.max_age}` : '+'} años)
            </span>
          )}
        </div>
      )}

      {/* Disciplina - solo para niveles principales, NO para subniveles */}
      {mode !== 'sublevel' && !isEditingSublevel && (
        <div className="form-group">
          <label htmlFor="discipline_id">Disciplina *</label>
          <select
            id="discipline_id"
            {...register('discipline_id', { 
              required: 'La disciplina es obligatoria',
              valueAsNumber: true
            })}
            className={errors.discipline_id ? 'input-error' : ''}
            disabled={!!initialData} // No permitir cambiar disciplina al editar
          >
            <option value="">Seleccionar disciplina...</option>
            {disciplines?.map((discipline) => (
              <option key={discipline.id} value={discipline.id}>
                {discipline.icon || '⚽'} {discipline.name}
              </option>
            ))}
          </select>
          {errors.discipline_id && <span className="error-message">{errors.discipline_id.message}</span>}
        </div>
      )}
      
      {/* Campo oculto para disciplina en subniveles (siempre viene del padre) */}
      {mode === 'sublevel' || isEditingSublevel ? (
        <input 
          type="hidden" 
          {...register('discipline_id', { 
            required: 'La disciplina es obligatoria',
            valueAsNumber: true
          })} 
        />
      ) : null}

      {/* Campo oculto para parent_level_id - siempre preservar para subniveles */}
      <input 
        type="hidden" 
        {...register('parent_level_id', {
          setValueAs: (value) => {
            // Si estamos editando un subnivel, siempre preservar el parent_level_id
            if (isEditingSublevel && initialData?.parent_level_id) {
              return initialData.parent_level_id;
            }
            // Si es modo subnivel nuevo, usar el parentLevel
            if (mode === 'sublevel' && parentLevel?.id) {
              return parentLevel.id;
            }
            return value === '' || value === 'null' || value === null ? null : parseInt(value);
          }
        })} 
      />

      <div className="form-group">
        <label htmlFor="name">
          {mode === 'sublevel' 
            ? 'Nombre del Subnivel de Habilidad *' 
            : categoryType === 'age' 
              ? 'Nombre de la Categoría *' 
              : 'Nombre del Nivel *'
          }
        </label>
        <input
          type="text"
          id="name"
          {...register('name', { required: 'El nombre es obligatorio' })}
          className={errors.name ? 'input-error' : ''}
          placeholder={
            mode === 'sublevel' 
              ? 'Ej: Principiante, Intermedio, Avanzado, Competitivo'
              : categoryType === 'age'
                ? 'Ej: Categoría 2012, Sub-12, Infantil, Juvenil'
                : 'Ej: Principiante, Intermedio, Avanzado, Profesional'
          }
        />
        {errors.name && <span className="error-message">{errors.name.message}</span>}
        <span className="hint-message">
          {mode === 'sublevel' 
            ? 'Nivel de habilidad dentro de esta categoría (ej: Principiante, Intermedio, Avanzado)'
            : categoryType === 'age'
              ? 'Nombre de la categoría por edad/año (ej: Categoría 2012, Sub-12, Infantil)'
              : 'Nombre del nivel de habilidad (ej: Principiante, Intermedio, Avanzado)'
          }
        </span>
      </div>

      {/* Descripción solo para niveles/categorías principales, NO para subniveles */}
      {mode !== 'sublevel' && !isEditingSublevel && (
        <div className="form-group">
          <label htmlFor="description">Descripción</label>
          <textarea
            id="description"
            {...register('description')}
            rows="3"
            placeholder="Descripción opcional..."
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="order">Orden *</label>
        <input
          type="number"
          id="order"
          onWheel={(e) => e.target.blur()}
          {...register('order', { 
            required: 'El orden es obligatorio',
            valueAsNumber: true,
            min: { value: 0, message: 'El orden debe ser mayor o igual a 0' }
          })}
          className={errors.order ? 'input-error' : ''}
          placeholder="0"
        />
        {errors.order && <span className="error-message">{errors.order.message}</span>}
        <span className="hint-message">
          El orden determina cómo se muestran (0 = primero, 1 = segundo, etc.)
        </span>
      </div>

      {/* Tipo de categoría - solo para modo level */}
      {mode !== 'sublevel' && !isEditingSublevel && (
        <div className="form-group">
          <label htmlFor="category_type">Tipo *</label>
          <select
            id="category_type"
            {...register('category_type', { required: 'El tipo es obligatorio' })}
            className={errors.category_type ? 'input-error' : ''}
            disabled={isEditingCategory} // No cambiar tipo de categoría existente
          >
            <option value="skill">🎯 Nivel de Habilidad (sin restricción de edad)</option>
            <option value="age">📂 Categoría por Edad/Año (con subniveles)</option>
          </select>
          {errors.category_type && <span className="error-message">{errors.category_type.message}</span>}
          <span className="hint-message">
            {categoryType === 'skill' 
              ? '🎯 Nivel simple basado en habilidad (ej: Principiante, Avanzado). Puede ser independiente o subnivel de una categoría.'
              : '📂 Categoría padre por edad/año. Puedes agregar subniveles de habilidad (ej: Categoría 2012 → Principiante, Competitivo)'
            }
          </span>
        </div>
      )}

      {/* Opción para asignar a una categoría padre (solo para skill sin padre y en modo level) */}
      {mode === 'level' && categoryType === 'skill' && !initialData && parentCategories && parentCategories.length > 0 && (
        <div className="form-group">
          <label htmlFor="parent_level_id">Categoría Padre (Opcional)</label>
          <select
            id="parent_level_id"
            {...register('parent_level_id', {
              setValueAs: (value) => value === '' || value === 'null' ? null : parseInt(value)
            })}
          >
            <option value="">Sin categoría padre (nivel independiente)</option>
            {parentCategories.map((cat) => {
              let categoryInfo = cat.name;
              if (cat.min_birth_year) {
                categoryInfo += ` (Año: ${cat.min_birth_year}${cat.max_birth_year && cat.max_birth_year !== cat.min_birth_year ? `-${cat.max_birth_year}` : ''})`;
              }
              if (cat.min_age) {
                categoryInfo += ` (Edad: ${cat.min_age}${cat.max_age ? `-${cat.max_age}` : '+'} años)`;
              }
              return (
                <option key={cat.id} value={cat.id}>
                  📂 {categoryInfo}
                </option>
              );
            })}
          </select>
          <span className="hint-message">
            Si seleccionas una categoría padre, este nivel será un subnivel de esa categoría.
          </span>
        </div>
      )}

      {/* Configuración de edad - solo para categorías (age) */}
      {categoryType === 'age' && mode !== 'sublevel' && (
        <>
          <div className="form-section">
            <h3>📅 Configuración de Edad/Año</h3>
            <p className="section-description">
              Define las restricciones de edad para esta categoría. Los estudiantes serán filtrados según estos criterios.
            </p>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="min_birth_year">Año mínimo desde:</label>
                <input
                  type="number"
                  id="min_birth_year"
                  {...register('min_birth_year', { 
                    setValueAs: (value) => value === '' || value === null ? null : parseInt(value),
                    min: { value: 1900, message: 'El año debe ser mayor a 1900' },
                    max: { value: new Date().getFullYear(), message: 'El año no puede ser mayor al año actual' },
                    validate: validateBirthYearRange('min_birth_year'),
                  })}
                  className={errors.min_birth_year ? 'input-error' : ''}
                  placeholder="Ej: 2012"
                />
                {errors.min_birth_year && <span className="error-message">{errors.min_birth_year.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="max_birth_year">Año máximo hasta:</label>
                <input
                  type="number"
                  id="max_birth_year"
                  {...register('max_birth_year', { 
                    setValueAs: (value) => value === '' || value === null ? null : parseInt(value),
                    min: { value: 1900, message: 'El año debe ser mayor a 1900' },
                    max: { value: new Date().getFullYear(), message: 'El año no puede ser mayor al año actual' },
                    validate: validateBirthYearRange('max_birth_year'),
                  })}
                  className={errors.max_birth_year ? 'input-error' : ''}
                  placeholder="Ej: 2012"
                />
                {errors.max_birth_year && <span className="error-message">{errors.max_birth_year.message}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="min_age">Edad Mínima</label>
                <input
                  type="number"
                  id="min_age"
                  {...register('min_age', { 
                    setValueAs: (value) => value === '' || value === null ? null : parseInt(value),
                    min: { value: 0, message: 'La edad debe ser mayor o igual a 0' },
                    max: { value: 100, message: 'La edad debe ser menor a 100' },
                    validate: validateAgeRange('min_age'),
                  })}
                  className={errors.min_age ? 'input-error' : ''}
                  placeholder="Ej: 11"
                />
                {errors.min_age && <span className="error-message">{errors.min_age.message}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="max_age">Edad Máxima</label>
                <input
                  type="number"
                  id="max_age"
                  {...register('max_age', { 
                    setValueAs: (value) => value === '' || value === null ? null : parseInt(value),
                    min: { value: 0, message: 'La edad debe ser mayor o igual a 0' },
                    max: { value: 100, message: 'La edad debe ser menor a 100' },
                    validate: validateAgeRange('max_age'),
                  })}
                  className={errors.max_age ? 'input-error' : ''}
                  placeholder="Ej: 12"
                />
                {errors.max_age && <span className="error-message">{errors.max_age.message}</span>}
              </div>
            </div>
            
            <div className="hint-box">
              <strong>💡 Ejemplos:</strong>
              <ul>
                <li><strong>SUB 12:</strong> Año mínimo desde = 2014, Año máximo hasta = 2016</li>
                <li><strong>Sub-18:</strong> Edad mínima = 16, Edad máxima = 18</li>
                <li><strong>8-10 años:</strong> Edad mínima = 8, Edad máxima = 10</li>
              </ul>
              <p>Puedes usar año de nacimiento O edad, o ambos.</p>
            </div>
          </div>

          <div className="info-box">
            <strong>📋 Después de crear esta categoría:</strong>
            <p>Podrás agregar subniveles de habilidad (Principiante, Intermedio, Competitivo, etc.) desde la lista de niveles.</p>
          </div>
        </>
      )}

      <div className="modal-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Guardando...' : initialData ? 'Actualizar' : mode === 'sublevel' ? 'Crear Subnivel' : 'Crear'}
        </button>
      </div>
    </form>
  );
};

export default LevelForm;
